/** Declarative per-delivery-method field registry for the notification editor.
 *
 * Single source of truth for which NDM fields each channel edits, which of
 * them are DTL template sources (validated + editable in TemplateCodeEditor),
 * and which rendering-type / standard-template columns belong to the channel.
 */
import { DELIVERY_METHODS } from '@/api/notifications';

export interface TemplateFieldSpec {
  /** NDM model field name (PATCH key). */
  field: string;
  label: string;
  /** Editor base: email bodies are HTML; everything else plain text. */
  baseLanguage: 'html' | 'text';
  /** Single-line sources (subjects) get a compact editor. */
  singleLine?: boolean;
}

export interface ChannelSpec {
  method: number;
  channel: 'email' | 'sms' | 'whatsapp' | 'chat' | 'airbnb' | null;
  /** Column carrying RENDER_STANDARD/RENDER_CUSTOM for this channel (null = always custom-ish). */
  renderingTypeField: string | null;
  /** Standard_Template FK column for this channel. */
  templateField: string | null;
  /** DTL template-source fields, in display order. */
  templateFields: TemplateFieldSpec[];
  /** Composition-aware channel (blocks/theme/test-render support). */
  composition: boolean;
}

export const CHANNEL_SPECS: Record<number, ChannelSpec> = {
  [DELIVERY_METHODS.EMAIL]: {
    method: DELIVERY_METHODS.EMAIL,
    channel: 'email',
    renderingTypeField: 'email_rendering_type',
    templateField: 'email_template',
    templateFields: [
      { field: 'email_subject', label: 'Subject', baseLanguage: 'text', singleLine: true },
      { field: 'email_message', label: 'Body (HTML)', baseLanguage: 'html' },
      { field: 'email_message_text', label: 'Body (plain text)', baseLanguage: 'text' },
    ],
    composition: true,
  },
  [DELIVERY_METHODS.SMS]: {
    method: DELIVERY_METHODS.SMS,
    channel: 'sms',
    renderingTypeField: 'sms_rendering_type',
    templateField: 'sms_template',
    templateFields: [{ field: 'sms_message', label: 'Message', baseLanguage: 'text' }],
    composition: true,
  },
  [DELIVERY_METHODS.WHATSAPP]: {
    method: DELIVERY_METHODS.WHATSAPP,
    channel: 'whatsapp',
    renderingTypeField: 'whatsapp_rendering_type',
    templateField: 'whatsapp_template',
    templateFields: [{ field: 'whatsapp_message', label: 'Message', baseLanguage: 'text' }],
    composition: true,
  },
  [DELIVERY_METHODS.CHAT]: {
    method: DELIVERY_METHODS.CHAT,
    channel: 'chat',
    renderingTypeField: 'chat_rendering_type',
    templateField: 'chat_template',
    templateFields: [{ field: 'chat_message', label: 'Message', baseLanguage: 'text' }],
    composition: true,
  },
  [DELIVERY_METHODS.AIRBNB]: {
    method: DELIVERY_METHODS.AIRBNB,
    channel: 'airbnb',
    renderingTypeField: 'airbnb_rendering_type',
    templateField: 'airbnb_template',
    templateFields: [{ field: 'airbnb_message', label: 'Message', baseLanguage: 'text' }],
    composition: true,
  },
  [DELIVERY_METHODS.SNS_TOPIC]: {
    method: DELIVERY_METHODS.SNS_TOPIC,
    channel: null,
    renderingTypeField: null,
    templateField: null,
    templateFields: [
      { field: 'sns_subject', label: 'SNS subject', baseLanguage: 'text', singleLine: true },
      { field: 'sns_message', label: 'SNS message', baseLanguage: 'text' },
    ],
    composition: false,
  },
  [DELIVERY_METHODS.SENTRY]: {
    method: DELIVERY_METHODS.SENTRY,
    channel: null,
    renderingTypeField: null,
    templateField: null,
    templateFields: [
      { field: 'sentry_message', label: 'Sentry message', baseLanguage: 'text', singleLine: true },
    ],
    composition: false,
  },
  // PUSH_NOTIFICATION and HTTP_REQUEST have no DTL text fields editable here
  // (HTTP request bodies/headers are JSON — shown read-only in v1).
  [DELIVERY_METHODS.PUSH_NOTIFICATION]: {
    method: DELIVERY_METHODS.PUSH_NOTIFICATION,
    channel: null,
    renderingTypeField: null,
    templateField: null,
    templateFields: [],
    composition: false,
  },
  [DELIVERY_METHODS.HTTP_REQUEST]: {
    method: DELIVERY_METHODS.HTTP_REQUEST,
    channel: null,
    renderingTypeField: null,
    templateField: null,
    templateFields: [],
    composition: false,
  },
};

// ---------------------------------------------------------------------------
// NDM enum label maps + per-channel settings descriptors.
//
// The value→label maps below mirror the Notification_Delivery_Method choice
// tuples in backend demo/models.py (EMAIL_DESTINATIONS / SMS_DESTINATIONS /
// WHATSAPP_DESTINATIONS / CHAT_DESTINATIONS / CHAT_ORIGINS /
// AIRBNB_DESTINATIONS / HTTP_METHODS) EXACTLY — keep them in sync when the
// model choices change.
// ---------------------------------------------------------------------------

export const EMAIL_DESTINATION_LABELS: Record<number, string> = {
  0: 'User',
  1: 'Support contact',
  2: 'Arbitrary address',
  3: 'All conversation members',
  4: 'Staff in conversation',
  5: 'Guides in conversation',
  6: 'Admins in conversation',
  7: 'Guests in conversation',
  8: 'OTA contact',
};

export const SMS_DESTINATION_LABELS: Record<number, string> = {
  0: 'User',
  1: 'Support contact',
  2: 'Arbitrary number',
  3: 'All conversation members',
  4: 'Staff in conversation',
  5: 'Guides in conversation',
  6: 'Admins in conversation',
  7: 'Guests in conversation',
  8: 'OTA contact',
};

export const WHATSAPP_DESTINATION_LABELS: Record<number, string> = {
  ...SMS_DESTINATION_LABELS,
};

export const CHAT_DESTINATION_LABELS: Record<number, string> = {
  0: 'Main office',
  1: 'Active tour (if any)',
  2: 'Event conversation',
  3: 'Arbitrary conversation',
  4: 'Group chat',
  5: 'Internal chat',
  6: 'Announcements',
};

export const CHAT_ORIGIN_LABELS: Record<number, string> = {
  0: 'Automated system message',
  1: 'User',
};

export const AIRBNB_DESTINATION_LABELS: Record<number, string> = {
  0: "Event conversation's associated Airbnb thread",
  1: 'Arbitrary Airbnb thread',
};

export const HTTP_METHODS = ['GET', 'HEAD', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;

/** Destination values with a dependent input (shared across email/sms/whatsapp). */
export const DEST_SUPPORT_CONTACT = 1;
export const DEST_ARBITRARY = 2;
/** chat_destination value that reveals the conversation-SID input. */
export const CHAT_TO_ARBITRARY_SID = 3;
/** chat_origin value that reveals the origin-user picker. */
export const CHAT_ORIGIN_USER = 1;
/** airbnb_destination value that reveals the thread-id input. */
export const AIRBNB_TO_ARBITRARY_THREAD = 1;

/** Declarative descriptor for the email/sms/whatsapp destination trio: one
 *  destination select + a support-contact picker (destination=1) + an
 *  arbitrary address/number input (destination=2) + the allow-override flag. */
export interface DestinationChannelSpec {
  destinationField: string;
  destinationLabels: Record<number, string>;
  supportField: string;
  supportLabel: string;
  addressField: string;
  addressLabel: string;
  allowOverrideField: string;
  allowOverrideLabel: string;
}

export const DESTINATION_CHANNELS: Partial<Record<number, DestinationChannelSpec>> = {
  [DELIVERY_METHODS.EMAIL]: {
    destinationField: 'email_destination',
    destinationLabels: EMAIL_DESTINATION_LABELS,
    supportField: 'email_support',
    supportLabel: 'Support contact',
    addressField: 'email_address',
    addressLabel: 'Arbitrary email address',
    allowOverrideField: 'email_allow_override',
    allowOverrideLabel: 'Allow event to override user email',
  },
  [DELIVERY_METHODS.SMS]: {
    destinationField: 'sms_destination',
    destinationLabels: SMS_DESTINATION_LABELS,
    supportField: 'sms_support',
    supportLabel: 'Support contact',
    addressField: 'sms_number',
    addressLabel: 'Arbitrary phone number',
    allowOverrideField: 'sms_allow_override',
    allowOverrideLabel: 'Allow event to override user phone number',
  },
  [DELIVERY_METHODS.WHATSAPP]: {
    destinationField: 'whatsapp_destination',
    destinationLabels: WHATSAPP_DESTINATION_LABELS,
    supportField: 'whatsapp_support',
    supportLabel: 'Support contact',
    addressField: 'whatsapp_number',
    addressLabel: 'Arbitrary phone number',
    allowOverrideField: 'whatsapp_allow_override',
    allowOverrideLabel: 'Allow event to override user phone number',
  },
};

/** The five email attachment switches, in dashboard display order. */
export const EMAIL_ATTACH_FIELDS: { field: string; label: string; hint: string }[] = [
  {
    field: 'email_attach_event_files',
    label: 'Event/calendar invite',
    hint: 'Embed an event/calendar invite',
  },
  {
    field: 'email_attach_booking_voucher',
    label: 'Booking voucher',
    hint: 'Attach the voucher provided by the booking system',
  },
  {
    field: 'email_attach_booking_invoice',
    label: 'Booking invoice',
    hint: 'Attach the invoice provided by the booking system',
  },
  {
    field: 'email_attach_participant_files',
    label: 'Participant files',
    hint: "Send files attached to a specific participant's booking",
  },
  {
    field: 'email_attach_notification_files',
    label: 'Notification files (Other)',
    hint: 'Generic attachments for every participant triggered by this notification',
  },
];

export function channelSpec(method: number): ChannelSpec {
  return (
    CHANNEL_SPECS[method] ?? {
      method,
      channel: null,
      renderingTypeField: null,
      templateField: null,
      templateFields: [],
      composition: false,
    }
  );
}

/** Every DTL-source field name across all channels (mirrors backend NF_TEMPLATE_FIELDS). */
export const ALL_TEMPLATE_FIELDS = Object.values(CHANNEL_SPECS).flatMap((spec) =>
  spec.templateFields.map((f) => f.field),
);
